import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  filterCatalogs,
  listRewardTypes,
  listRewards,
} from "../../../../../../../graphql/queries/filterCatalogs";
import { listRewardUnits } from "../../../../../../../graphql/queries/listRewardUnits";
import { formatCatalogsForDisplay } from "../common";

const CATALOGS_PAGE_SIZE = 100;
const CATALOG_IDS_BATCH_SIZE = 50;

export default function useAddCatalogRewardsData() {
  const client = useApolloClient();
  const [rewardsData, setRewardsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const callListRewards = useCallback(
    ({ variables, fetchPolicy }) => {
      const { filter, page, size } = variables;
      const catalogIds = filter?.catalogs || [];

      // Increment request id so stale responses are ignored
      const currentRequestId = ++requestIdRef.current;

      if (catalogIds.length <= CATALOG_IDS_BATCH_SIZE) {
        // No batching needed — single query
        setLoading(true);
        client
          .query({ query: listRewards, variables, fetchPolicy })
          .then(({ data }) => {
            if (requestIdRef.current === currentRequestId) {
              setRewardsData(data);
            }
          })
          .finally(() => {
            if (requestIdRef.current === currentRequestId) {
              setLoading(false);
            }
          });
        return;
      }

      // Split catalog IDs into batches of CATALOG_IDS_BATCH_SIZE
      const batches = [];
      for (let i = 0; i < catalogIds.length; i += CATALOG_IDS_BATCH_SIZE) {
        batches.push(catalogIds.slice(i, i + CATALOG_IDS_BATCH_SIZE));
      }

      setLoading(true);
      Promise.all(
        batches.map((batchIds) =>
          client.query({
            query: listRewards,
            variables: {
              filter: { ...filter, catalogs: batchIds },
              page,
              size,
            },
            fetchPolicy,
          })
        )
      )
        .then((responses) => {
          if (requestIdRef.current !== currentRequestId) return;

          // Merge items from all batches and deduplicate by reward id
          const itemsById = new Map();
          let total = 0;

          responses.forEach(({ data }) => {
            const batch = data?.listRewards;
            if (!batch) return;
            total += batch.total || 0;
            (batch.items || []).forEach((item) => {
              if (!itemsById.has(item.id)) {
                itemsById.set(item.id, item);
              }
            });
          });

          setRewardsData({
            listRewards: {
              items: Array.from(itemsById.values()),
              total,
              first: 0,
              last: itemsById.size,
              self: 0,
            },
          });
        })
        .finally(() => {
          if (requestIdRef.current === currentRequestId) {
            setLoading(false);
          }
        });
    },
    [client]
  );
  const catalogFilters = useMemo(
    () => ({
      status: ["APPROVED"],
      selectableOnly: true,
      limit: CATALOGS_PAGE_SIZE,
      offset: 0,
      toAttach: true,
    }),
    []
  );
  const { data: catalogList, fetchMore: fetchMoreCatalogs } = useQuery(
    filterCatalogs,
    {
      variables: {
        filters: catalogFilters,
      },
    }
  );
  const [catalogItems, setCatalogItems] = useState([]);
  const { data: rewardUnitsData } = useQuery(listRewardUnits, {
    variables: {
      size: 100,
    },
  });
  const { data: rewardTypesData } = useQuery(listRewardTypes, {
    variables: {
      page: 0,
      size: 100,
    },
  });

  useEffect(() => {
    const firstPage = catalogList?.filterCatalogs;
    if (!firstPage) return;

    const firstPageItems = firstPage.items || [];
    const total = firstPage.total || firstPageItems.length;
    const totalPages = Math.ceil(total / CATALOGS_PAGE_SIZE);

    if (totalPages <= 1) {
      setCatalogItems(firstPageItems);
      return;
    }

    let isMounted = true;

    const fetchAllCatalogs = async () => {
      const remainingPages = Array.from(
        { length: totalPages - 1 },
        (_, index) => (index + 1) * CATALOGS_PAGE_SIZE
      );

      const responses = await Promise.all(
        remainingPages.map((offset) =>
          fetchMoreCatalogs({
            variables: {
              filters: {
                ...catalogFilters,
                offset,
              },
            },
          })
        )
      );

      if (!isMounted) return;

      const remainingItems = responses.flatMap(
        (response) => response?.data?.filterCatalogs?.items || []
      );
      const catalogsById = new Map();

      [...firstPageItems, ...remainingItems].forEach((catalog) => {
        if (catalog?.catalogId) {
          catalogsById.set(catalog.catalogId, catalog);
        }
      });

      setCatalogItems(Array.from(catalogsById.values()));
    };

    fetchAllCatalogs().catch(() => {
      if (isMounted) {
        setCatalogItems(firstPageItems);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [catalogFilters, catalogList?.filterCatalogs, fetchMoreCatalogs]);

  const displayCatalogs = useMemo(
    () => formatCatalogsForDisplay(catalogItems),
    [catalogItems]
  );

  const rewardUnitAndTypeMaps = useMemo(() => {
    const unitsMap = {};
    rewardUnitsData?.listRewardUnits?.items?.forEach((unit) => {
      unitsMap[unit.id] = unit.unit;
    });

    const typesMap = {};
    rewardTypesData?.listRewardType?.items?.forEach((type) => {
      typesMap[type.id] = type.name;
    });

    return { unitsMap, typesMap };
  }, [
    rewardUnitsData?.listRewardUnits?.items,
    rewardTypesData?.listRewardType?.items,
  ]);

  return {
    callListRewards,
    catalogItems,
    displayCatalogs,
    rewardsData,
    rewardsLoading: loading,
    rewardUnitAndTypeMaps,
  };
}
